import { IconStarFilled } from '@tabler/icons-react'
import classNames from 'classnames'
import Typography, { TypographyTag, TypographyVariant } from '../Typography'
import styles from './CustomerMyRewardsProgressCard.module.css'
import { CustomerMyRewardsProgressCardProps } from './CustomerMyRewardsProgressCard.types'

const CustomerMyRewardsProgressCard = ({
  currentPoints,
  tiers = [],
  showRemainingPoints = true,
}: CustomerMyRewardsProgressCardProps) => {
  const _tiers = [...tiers]

  const maxPoint = _tiers.length > 0 ? _tiers.splice(-1)[0] : { points: 0 }
  const pointsDifference = (maxPoint?.points ?? 0) - (currentPoints ?? 0)

  const calcPercentage = (value: number, total: number) => {
    if (!total) {
      return 0
    }

    const _hundred = 100
    return (_hundred * (value || 0)) / total
  }

  const getPostMask = (pos: number) => {
    const _offset = 0.18
    const _step = 0.01
    const _hundred = 100
    const _maskOpacity = _offset * (1 - pos / _hundred) + _step
    return {
      backgroundImage: `radial-gradient(96.69% 271.43% at 13.26% 16%, rgba(255, 255, 255, ${_maskOpacity}) 0%, rgba(255, 255, 255, ${
        _maskOpacity - _step
      }) 100%)`,
    }
  }

  return (
    <div
      className={classNames(
        styles.root,
        'p-4 pb-6 lg:p-8 lg:pt-4 lg:pl-6 rounded-md'
      )}>
      <div className='flex items-center lg:items-end justify-between'>
        <div className='flex items-start gap-x-1'>
          <Typography
            variant={TypographyVariant.BodyRegularBold}
            tag={TypographyTag.p}>
            <span
              className={classNames(
                styles.point,
                'block font-bold text-5xl lg:text-6xl'
              )}>
              {currentPoints > 0 ? currentPoints : 0}
            </span>
          </Typography>
          <div className={styles.unit}>
            <Typography
              variant={TypographyVariant.BodyLargeBold}
              tag={TypographyTag.p}>
              PTS
            </Typography>
          </div>
        </div>
        {showRemainingPoints && (
          <div className='lg:mb-4 text-right'>
            <Typography
              variant={TypographyVariant.BodyRegular}
              tag={TypographyTag.p}>
              <span className='block lg:text-lg lg:leading-6'>
                <span className='font-extrabold'>
                  {pointsDifference > 0 ? pointsDifference : 0} Points
                </span>{' '}
                to get
                <span className='block'> your next Reward!</span>
              </span>
            </Typography>
          </div>
        )}
      </div>
      <div className='lg:pl-2'>
        <div className={styles.progress}>
          <span
            className={styles.bar}
            style={{
              width: `${calcPercentage(currentPoints, maxPoint.points)}%`,
            }}
          />
          {_tiers.map((tier, index) => {
            const _percentage = calcPercentage(tier.points, maxPoint.points)
            const _isDisabled = currentPoints < tier.points
            const _isStar = tier.points % 200 === 0 && tier.points > 0

            return (
              <div
                key={index}
                className={classNames(
                  styles.tier,
                  'flex flex-col items-center',
                  _isStar ? '!top-0' : ''
                )}
                style={{ left: `${_percentage}%` }}>
                {_isStar ? (
                  <span className={classNames(styles.star, styles.starActive)}>
                    <IconStarFilled size={24} />
                  </span>
                ) : (
                  <span
                    className={classNames(
                      styles.dot,
                      _isDisabled ? '' : styles.dotActive,
                      'block'
                    )}
                    style={getPostMask(_percentage)}
                  />
                )}

                <div className={classNames(_isStar ? 'mt-2' : 'mt-5')}>
                  <Typography
                    tag={TypographyTag.p}
                    variant={TypographyVariant.BodyRegular}>
                    <span
                      className={_isDisabled ? 'opacity-20' : 'font-extrabold'}>
                      {tier.points}
                    </span>
                  </Typography>
                </div>
              </div>
            )
          })}
          <div
            className={classNames(
              styles.starTier,
              'flex flex-col items-center'
            )}>
            <span
              className={classNames(
                styles.star,
                maxPoint?.points > currentPoints ? '' : styles.starActive
              )}>
              <IconStarFilled size={24} />
            </span>
            <div className='mt-2'>
              <Typography
                tag={TypographyTag.p}
                variant={TypographyVariant.BodyRegular}>
                <span
                  className={
                    maxPoint?.points > currentPoints
                      ? 'opacity-20'
                      : 'font-extrabold'
                  }>
                  {maxPoint.points}
                </span>
              </Typography>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CustomerMyRewardsProgressCard
